import { PromiseWorkerPool } from '@art-suite/art-core-ts';
import { useState } from 'react';

import { JobLifecycleStatus, KaiProApiClient } from '@kaiizen/kai-pro-api';

import { useKaiProApiClient } from '../useKaiProApiClient';
import { pollAsyncJob } from './pollAsyncJob';

const PURCHASE_ORDER_DOWNLOAD_CONCURRENCY = 10;
const TERMINAL_STATUSES: JobLifecycleStatus[] = ['completed', 'failed'];

export type PurchaseOrderExcelFile = { scanId: string; filename: string; data: Uint8Array };
export type PurchaseOrderExcelFailure = { scanId: string; error: string };
export type PurchaseOrderExcelDownloadProgress = { done: number; total: number };

type ExcelExportJob = {
  lifecycleStatus: JobLifecycleStatus;
  output?: { error?: unknown; pdfPresignedUrl?: string; s3Key?: string };
};

const getErrorMessage = (error: unknown) => error instanceof Error ? error.message : String(error);

/** Starts the scan's full-scope purchase order Excel export (or reuses a current one), waits for it, then downloads the file. */
const fetchPurchaseOrderExcel = async (kaiProApiClient: KaiProApiClient, scanId: string): Promise<PurchaseOrderExcelFile> => {
  const job = await pollAsyncJob({
    start: () => kaiProApiClient.exportScopePurchaseOrder(scanId, { reportType: 'fullScope' }) as unknown as Promise<ExcelExportJob>,
    isTerminal: ({ lifecycleStatus }) => TERMINAL_STATUSES.includes(lifecycleStatus),
    jobName: 'Purchase order export',
  });
  const { pdfPresignedUrl, s3Key, error } = job.output ?? {};
  if (error || !pdfPresignedUrl) throw new Error(error ? getErrorMessage(error) : 'The export finished without a file');
  const response = await fetch(pdfPresignedUrl);
  if (!response.ok) throw new Error(`Download failed: ${response.status} ${response.statusText}`);
  return { scanId, filename: s3Key?.split('/').pop() ?? `${scanId}.xlsx`, data: new Uint8Array(await response.arrayBuffer()) };
};

/**
 * Downloads each scan's full-scope purchase order Excel file, 10 at a time. An export that is already generated and current
 * comes back at once; the export workers generate the rest. A scan that fails is reported, not thrown.
 *
 * @example
 * ```ts
 * const { download, progress } = usePurchaseOrderExcelDownloads();
 * const { files, failures } = await download(scanIds);
 * ```
 */
export const usePurchaseOrderExcelDownloads = () => {
  const kaiProApiClient = useKaiProApiClient();
  const [progress, setProgress] = useState<PurchaseOrderExcelDownloadProgress>();

  const download = async (scanIds: string[]) => {
    const files: PurchaseOrderExcelFile[] = [];
    const failures: PurchaseOrderExcelFailure[] = [];
    setProgress({ done: 0, total: scanIds.length });
    const pool = new PromiseWorkerPool(PURCHASE_ORDER_DOWNLOAD_CONCURRENCY);
    for (const scanId of scanIds) {
      await pool.queue(() =>
        fetchPurchaseOrderExcel(kaiProApiClient, scanId)
          .then(file => files.push(file), error => failures.push({ scanId, error: getErrorMessage(error) }))
          .finally(() => setProgress(previous => previous && { ...previous, done: previous.done + 1 }))
      );
    }
    await pool.end();
    return { files, failures };
  };

  return { download, progress };
};
